import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  RefreshCw, 
  ArrowLeft, 
  Mail, 
  Building2, 
  User, 
  Calendar,
  MessageSquare,
  CheckCircle,
  Clock,
  Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_8a27fd21-2917-4128-b502-8e2ad7b4f186/artifacts/rr8eu6u8_EC%20full%20-%20without%20border%20%281%29.png";

const AdminDashboard = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/inquiries`);
      setInquiries(response.data);
    } catch (error) {
      toast.error("Failed to load inquiries");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/inquiries/${id}/status`, { status: newStatus });
      setInquiries(inquiries.map(inq => 
        inq.id === id ? { ...inq, status: newStatus } : inq
      ));
      toast.success("Status updated successfully");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteInquiry = async (id) => {
    try {
      await axios.delete(`${API}/inquiries/${id}`);
      setInquiries(inquiries.filter(inq => inq.id !== id));
      toast.success("Inquiry deleted");
    } catch (error) {
      toast.error("Failed to delete inquiry");
    }
  };

  const filteredInquiries = filter === "all" 
    ? inquiries 
    : inquiries.filter(inq => inq.status === filter);

  const getStatusBadge = (status) => {
    const styles = {
      new: "bg-[#056839] text-white hover:bg-[#04502b]",
      contacted: "bg-[#69A488] text-white hover:bg-[#5a9479]",
      closed: "bg-gray-400 text-white hover:bg-gray-500"
    };
    return styles[status] || styles.new;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stats = {
    total: inquiries.length,
    new: inquiries.filter(i => i.status === 'new').length,
    contacted: inquiries.filter(i => i.status === 'contacted').length,
    closed: inquiries.filter(i => i.status === 'closed').length
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50" data-testid="admin-header">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2" data-testid="admin-logo-link">
              <img src={LOGO} alt="ElectroCycle" className="w-[100px] md:w-[130px] h-auto object-contain" style={{ border: 'none', outline: 'none' }} />
            </a>
            <div className="h-8 w-px bg-gray-200"></div>
            <h1 className="text-xl font-semibold text-[#3F3F3F]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Admin Dashboard
            </h1>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={fetchInquiries}
              disabled={loading}
              className="flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </Button>
            <a href="/">
              <Button variant="ghost" className="flex items-center gap-2" data-testid="back-to-site-btn">
                <ArrowLeft size={16} />
                Back to Site
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="stats-cards">
          <div className="bg-white rounded-sm border border-gray-100 p-6 shadow-sm" data-testid="stat-total">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Inquiries</p>
                <p className="text-3xl font-bold text-[#3F3F3F]" style={{ fontFamily: 'Manrope, sans-serif' }}>{stats.total}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-gray-100 flex items-center justify-center">
                <MessageSquare size={24} className="text-gray-500" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-sm border border-gray-100 p-6 shadow-sm" data-testid="stat-new">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">New</p>
                <p className="text-3xl font-bold text-[#056839]" style={{ fontFamily: 'Manrope, sans-serif' }}>{stats.new}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[#D2E4D6] flex items-center justify-center">
                <Clock size={24} className="text-[#056839]" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-sm border border-gray-100 p-6 shadow-sm" data-testid="stat-contacted">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Contacted</p>
                <p className="text-3xl font-bold text-[#69A488]" style={{ fontFamily: 'Manrope, sans-serif' }}>{stats.contacted}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[#D2E4D6] flex items-center justify-center">
                <Mail size={24} className="text-[#69A488]" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-sm border border-gray-100 p-6 shadow-sm" data-testid="stat-closed">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Closed</p>
                <p className="text-3xl font-bold text-gray-400" style={{ fontFamily: 'Manrope, sans-serif' }}>{stats.closed}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-gray-100 flex items-center justify-center">
                <CheckCircle size={24} className="text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Filter and Table */}
        <div className="bg-white rounded-sm border border-gray-100 shadow-sm" data-testid="inquiries-table-container">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#3F3F3F]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Inquiries
            </h2>
            <Select value={filter} onValueChange={setFilter} data-testid="filter-select">
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Inquiries</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="p-12 text-center" data-testid="loading-state">
              <RefreshCw size={32} className="animate-spin mx-auto text-[#056839] mb-4" />
              <p className="text-gray-500">Loading inquiries...</p>
            </div>
          ) : filteredInquiries.length === 0 ? (
            <div className="p-12 text-center" data-testid="empty-state">
              <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No inquiries found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="inquiries-table">
                <TableHeader>
                  <TableRow className="bg-[#F9FAFB]">
                    <TableHead className="font-semibold text-[#3F3F3F]">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} />
                        Date
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#3F3F3F]">
                      <div className="flex items-center gap-2">
                        <User size={14} />
                        Name
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#3F3F3F]">
                      <div className="flex items-center gap-2">
                        <Building2 size={14} />
                        Company
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#3F3F3F]">
                      <div className="flex items-center gap-2">
                        <Mail size={14} />
                        Email
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#3F3F3F]">Message</TableHead>
                    <TableHead className="font-semibold text-[#3F3F3F]">Status</TableHead>
                    <TableHead className="font-semibold text-[#3F3F3F]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInquiries.map((inquiry) => (
                    <TableRow key={inquiry.id} className="hover:bg-[#F9FAFB]" data-testid={`inquiry-row-${inquiry.id}`}>
                      <TableCell className="text-sm text-gray-600">
                        {formatDate(inquiry.created_at)}
                      </TableCell>
                      <TableCell className="font-medium text-[#3F3F3F]">
                        {inquiry.full_name}
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {inquiry.company_name}
                      </TableCell>
                      <TableCell>
                        <a 
                          href={`mailto:${inquiry.email}`} 
                          className="text-[#056839] hover:underline"
                          data-testid={`email-link-${inquiry.id}`}
                        >
                          {inquiry.email}
                        </a>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-gray-600" title={inquiry.message}>
                        {inquiry.message || "-"}
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={inquiry.status} 
                          onValueChange={(value) => updateStatus(inquiry.id, value)}
                          data-testid={`status-select-${inquiry.id}`}
                        >
                          <SelectTrigger className="w-[120px] border-none p-0">
                            <Badge className={getStatusBadge(inquiry.status)}>
                              {inquiry.status.charAt(0).toUpperCase() + inquiry.status.slice(1)}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-btn-${inquiry.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Inquiry</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this inquiry from {inquiry.company_name}? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => deleteInquiry(inquiry.id)}
                                className="bg-red-500 hover:bg-red-600"
                                data-testid={`confirm-delete-btn-${inquiry.id}`}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
